// v1 DTMS 의 graphic.data 는 셀 하나를 2x4 핀으로 묶어 1바이트에 담은 hex 문자열이다.
// 닷패드320(60x40)과 닷패드768(96x64)이 같은 패킹을 쓴다.
// DotCanvasNavigation.convertHexToBits / DotpadSDK.Make_DTM_Data 와 같은 배치를 만든다.

function hexToBytes(hex) {
    if (typeof hex !== "string" || hex.length % 2 !== 0 || !/^[0-9a-f]*$/i.test(hex)) {
        throw new TypeError("legacy graphic data must be an even-length hexadecimal string")
    }
    const bytes = new Uint8Array(hex.length / 2)
    for (let offset = 0; offset < hex.length; offset += 2) {
        bytes[offset / 2] = Number.parseInt(hex.slice(offset, offset + 2), 16)
    }
    return bytes
}

export function decodeLegacyRaster(graphicData, surface) {
    const pinCount = surface.width * surface.height
    if (graphicData == null || graphicData.length === 0) {
        return new Uint8Array(pinCount)
    }

    if (Array.isArray(graphicData) || graphicData instanceof Uint8Array) {
        if (graphicData.length !== pinCount) {
            throw new RangeError(`legacy pin bitmap length must be ${pinCount}`)
        }
        return Uint8Array.from(graphicData, value => value ? 1 : 0)
    }

    const byteArray = hexToBytes(graphicData)
    if (byteArray.length * 8 !== pinCount || surface.width % 2 !== 0 || surface.height % 4 !== 0) {
        throw new RangeError("legacy hexadecimal raster does not match the page surface")
    }

    const raster = new Uint8Array(pinCount)
    const halfWidth = surface.width / 2
    for (let index = 0; index < byteArray.length; index++) {
        const startIndex = Math.floor(index / halfWidth) * surface.width * 4
            + (index % halfWidth) * 2
        for (let bit = 0; bit < 8; bit++) {
            const pinIndex = startIndex + (bit % 4) * surface.width + Math.floor(bit / 4)
            raster[pinIndex] = byteArray[index] & (1 << bit) ? 1 : 0
        }
    }
    return raster
}

export function encodeLegacyRaster(raster, surface) {
    const pinCount = surface.width * surface.height
    if (!(raster instanceof Uint8Array) && !Array.isArray(raster)) {
        throw new TypeError("editor raster must be a Uint8Array or array")
    }
    if (raster.length !== pinCount || surface.width % 2 !== 0 || surface.height % 4 !== 0) {
        throw new RangeError("editor raster does not match a legacy-compatible page surface")
    }

    const halfWidth = surface.width / 2
    const bytes = new Uint8Array(pinCount / 8)
    for (let index = 0; index < bytes.length; index++) {
        const startIndex = Math.floor(index / halfWidth) * surface.width * 4
            + (index % halfWidth) * 2
        let byte = 0
        for (let bit = 0; bit < 8; bit++) {
            const pinIndex = startIndex + (bit % 4) * surface.width + Math.floor(bit / 4)
            if (raster[pinIndex]) byte |= 1 << bit
        }
        bytes[index] = byte
    }
    return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("")
}

/*
 모바일 캔버스 앱(iOS·Android)은 핀마다 색을 graphic.colors 에 적는다.
 - 지금 앱이 쓰는 꼴: 핀 하나에 한 글자(60x40 이면 2400자). '0' 색 없음, K 검정, B 파랑, Y 노랑, R 빨강.
   글자 차례는 graphic.data 의 바이트 차례이고, 한 바이트(2x4 칸) 안에서는 bit7 → bit0 이다.
 - 옛 앱이 쓰던 꼴: 핀 하나에 4자(ARGB 한 자리씩, 9600자), 줄 차례. 0000 이 색 없음이다.
 색 번호는 앱과 같다(0 K 검정 · 1 B 파랑 · 2 Y 노랑 · 3 R 빨강 = layerState 의 LAYER_COLOR_PALETTE).
 */
/** 색 없음('0'). 내려간 핀이거나, 앱이 색을 적지 않은 핀이다. */
export const NO_LEGACY_COLOR = -1
const LEGACY_COLOR_LETTERS = Object.freeze(["K", "B", "Y", "R"])
const LEGACY_COLOR_INDEX_BY_LETTER = Object.freeze({K: 0, B: 1, Y: 2, R: 3})
const LEGACY_ARGB_COLOR_INDEX = Object.freeze({F07F: 1, FFC0: 2, FF33: 3})

// 모르는 글자는 검정으로 본다(앱도 팔레트 밖 색은 검정으로 다룬다).
function legacyColorIndexOf(letter) {
    if (letter === "0") return NO_LEGACY_COLOR
    return LEGACY_COLOR_INDEX_BY_LETTER[letter.toUpperCase()] ?? 0
}

/**
 graphic.colors 를 핀 차례(줄 차례) 색 번호로 푼다. 색 없음은 NO_LEGACY_COLOR 다.
 - returns: {Int8Array|null} 길이가 두 꼴 어느 쪽과도 맞지 않으면 null
 */
export function decodeLegacyColors(colors, surface) {
    if (typeof colors !== "string") return null
    const pinCount = surface.width * surface.height
    if (colors.length === pinCount * 4) {
        const codes = new Int8Array(pinCount)
        for (let pinIndex = 0; pinIndex < pinCount; pinIndex++) {
            const token = colors.slice(pinIndex * 4, pinIndex * 4 + 4).toUpperCase()
            codes[pinIndex] = token === "0000" ? NO_LEGACY_COLOR : LEGACY_ARGB_COLOR_INDEX[token] ?? 0
        }
        return codes
    }
    if (colors.length !== pinCount || surface.width % 2 !== 0 || surface.height % 4 !== 0) return null

    const codes = new Int8Array(pinCount)
    const halfWidth = surface.width / 2
    for (let index = 0; index < pinCount / 8; index++) {
        const startIndex = Math.floor(index / halfWidth) * surface.width * 4
            + (index % halfWidth) * 2
        for (let bit = 0; bit < 8; bit++) {
            const pinIndex = startIndex + (bit % 4) * surface.width + Math.floor(bit / 4)
            codes[pinIndex] = legacyColorIndexOf(colors[index * 8 + 7 - bit])
        }
    }
    return codes
}

/** decodeLegacyColors 의 반대. 늘 지금 앱이 쓰는 꼴(핀당 한 글자)로 적는다. */
export function encodeLegacyColors(codes, surface) {
    const pinCount = surface.width * surface.height
    if (!codes || codes.length !== pinCount || surface.width % 2 !== 0 || surface.height % 4 !== 0) {
        throw new RangeError("pin colors do not match a legacy-compatible page surface")
    }

    const letters = new Array(pinCount)
    const halfWidth = surface.width / 2
    for (let index = 0; index < pinCount / 8; index++) {
        const startIndex = Math.floor(index / halfWidth) * surface.width * 4
            + (index % halfWidth) * 2
        for (let bit = 0; bit < 8; bit++) {
            const pinIndex = startIndex + (bit % 4) * surface.width + Math.floor(bit / 4)
            const code = codes[pinIndex]
            letters[index * 8 + 7 - bit] = code < 0 ? "0" : LEGACY_COLOR_LETTERS[code] ?? "K"
        }
    }
    return letters.join("")
}
